/**
 * The files inside ZIP archives the user chose (ANON-04), read without loading the archive: the
 * central directory is read from the end, and each entry is read and inflated only when the pipeline
 * reaches it, so archives far larger than memory work. ZIP64 archives (over 4 GB or 65,535 entries)
 * are read too; encrypted entries are not.
 */

/** What the pipeline reads: a chosen file or a file inside an archive. */
export interface Source {
  readonly name: string
  /** The path shown to the user; for an archive entry, the archive's name and the entry's path. */
  readonly webkitRelativePath: string
  readonly size: number
  readonly lastModified: number
  arrayBuffer(): Promise<ArrayBuffer>
}

/** A file that cannot be read, with the code the page explains. */
export class UnreadableError extends Error {
  constructor(readonly code: string) {
    super(code)
  }
}

const END = 0x06054b50
const END64 = 0x06064b50
const END64_LOCATOR = 0x07064b50
const CENTRAL = 0x02014b50
const LOCAL = 0x04034b50
const MAX16 = 0xffff
const MAX32 = 0xffffffff

const isZip = (file: File) => /\.zip$/i.test(file.name) || /zip/.test(file.type)

/** The chosen files, with every archive replaced by the files inside it. */
export async function expandArchives(files: readonly File[]): Promise<Source[]> {
  const sources: Source[] = []
  for (const file of files) sources.push(...(isZip(file) ? await zipEntries(file) : [file]))
  return sources
}

async function view(blob: Blob, start: number, end: number): Promise<DataView> {
  return new DataView(await blob.slice(start, end).arrayBuffer())
}

const unreadable = (): never => {
  throw new UnreadableError('zip_unreadable')
}

/** The files of one archive, from its central directory. */
export async function zipEntries(zip: File): Promise<Source[]> {
  // The end record is the last 22 bytes, followed by a comment of at most 65,535 bytes.
  const tailStart = Math.max(0, zip.size - 22 - MAX16)
  const tail = await view(zip, tailStart, zip.size)
  let end = tail.byteLength - 22
  while (end >= 0 && tail.getUint32(end, true) !== END) end--
  if (end < 0) unreadable()
  let count = tail.getUint16(end + 10, true)
  let size = tail.getUint32(end + 12, true)
  let offset = tail.getUint32(end + 16, true)
  if (count === MAX16 || size === MAX32 || offset === MAX32) {
    // ZIP64: a locator just before the end record points to the ZIP64 end record.
    const locator = end - 20
    if (locator < 0 || tail.getUint32(locator, true) !== END64_LOCATOR) unreadable()
    const at = Number(tail.getBigUint64(locator + 8, true))
    const record = await view(zip, at, at + 56)
    if (record.getUint32(0, true) !== END64) unreadable()
    count = Number(record.getBigUint64(32, true))
    size = Number(record.getBigUint64(40, true))
    offset = Number(record.getBigUint64(48, true))
  }

  const directory = await view(zip, offset, offset + size)
  const names = new TextDecoder() // UTF-8; older archives' code page 437 agrees with it for ASCII names
  const entries: Source[] = []
  for (let at = 0, n = 0; n < count; n++) {
    if (at + 46 > directory.byteLength || directory.getUint32(at, true) !== CENTRAL) unreadable()
    const flags = directory.getUint16(at + 8, true)
    const method = directory.getUint16(at + 10, true)
    let compressed = directory.getUint32(at + 20, true)
    let uncompressed = directory.getUint32(at + 24, true)
    const nameLength = directory.getUint16(at + 28, true)
    const extraLength = directory.getUint16(at + 30, true)
    const commentLength = directory.getUint16(at + 32, true)
    let local = directory.getUint32(at + 42, true)
    const path = names.decode(new Uint8Array(directory.buffer, at + 46, nameLength))
    // The ZIP64 extra field holds, in this order, the values that did not fit in 32 bits.
    for (let extra = at + 46 + nameLength, extraEnd = extra + extraLength; extra + 4 <= extraEnd; ) {
      const id = directory.getUint16(extra, true)
      const length = directory.getUint16(extra + 2, true)
      if (id === 0x0001) {
        let field = extra + 4
        const next = () => {
          const value = Number(directory.getBigUint64(field, true))
          field += 8
          return value
        }
        if (uncompressed === MAX32) uncompressed = next()
        if (compressed === MAX32) compressed = next()
        if (local === MAX32) local = next()
      }
      extra += 4 + length
    }
    at += 46 + nameLength + extraLength + commentLength
    if (path.endsWith('/') || path.startsWith('__MACOSX/')) continue // folders and macOS metadata
    entries.push(new ZipEntry(zip, path, { flags, method, compressed, uncompressed, local }))
  }
  return entries
}

interface Entry {
  flags: number
  method: number
  compressed: number
  uncompressed: number
  local: number
}

class ZipEntry implements Source {
  readonly name: string
  readonly webkitRelativePath: string
  readonly size: number
  readonly lastModified: number

  constructor(
    private readonly zip: File,
    path: string,
    private readonly entry: Entry,
  ) {
    this.name = path.slice(path.lastIndexOf('/') + 1)
    this.webkitRelativePath = `${zip.name}/${path}`
    this.size = entry.uncompressed
    this.lastModified = zip.lastModified
  }

  async arrayBuffer(): Promise<ArrayBuffer> {
    const { flags, method, compressed, local } = this.entry
    if (flags & 1) throw new UnreadableError('zip_encrypted')
    const header = await view(this.zip, local, local + 30)
    if (header.getUint32(0, true) !== LOCAL) unreadable()
    const start = local + 30 + header.getUint16(26, true) + header.getUint16(28, true)
    const data = this.zip.slice(start, start + compressed)
    if (method === 0) return data.arrayBuffer() // stored
    if (method === 8) return new Response(data.stream().pipeThrough(new DecompressionStream('deflate-raw'))).arrayBuffer()
    throw new UnreadableError('zip_method_unsupported')
  }
}
